/**
 * BeHeard's onboarding questions (goal / experience / role), shared by the
 * first-run OnboardingModal and the ?retake wizard. Each answer is saved on
 * POST /onboarding/me and builds the user's assessment path:
 *   goal       → primaryIntent  (which skills the path targets)
 *   experience → experienceLevel (difficulty of the assessments)
 *   role       → currentRole / targetRole (personalizes the questions; optional)
 */
import type { LucideIcon } from 'lucide-react';
import { Award, Briefcase, Handshake, Mic, Presentation, Sprout, TrendingUp, Trophy } from 'lucide-react';
import type { ExperienceLevel, PrimaryIntent } from '../../store/api/onboardingApi';

export interface OnboardingOption<V extends string> {
  value: V;
  label: string;
  description: string;
  icon: LucideIcon;
}

export const INTENT_OPTIONS: OnboardingOption<PrimaryIntent>[] = [
  { value: 'improve_speaking', label: 'Improve my speaking skills', description: 'Build confidence and clarity in everyday communication', icon: Mic },
  { value: 'get_hired', label: 'Get hired for a new role', description: 'Prepare for interviews and showcase your expertise', icon: Briefcase },
  { value: 'prove_expertise', label: 'Show my domain expertise', description: 'Practise the conversations of your field and share what you choose', icon: Award },
  { value: 'close_more_deals', label: 'Close more deals', description: 'Master objection handling and sales conversations', icon: Handshake },
  { value: 'better_public_speaking', label: 'Become a better public speaker', description: 'Improve presentations and public speaking skills', icon: Presentation },
];

export const EXPERIENCE_OPTIONS: OnboardingOption<ExperienceLevel>[] = [
  { value: 'beginner', label: 'Beginner', description: "Build a solid base, fast", icon: Sprout },
  { value: 'intermediate', label: 'Intermediate', description: 'Turn experience into confidence', icon: TrendingUp },
  { value: 'advanced', label: 'Advanced', description: "Sharpen the edge that sets you apart", icon: Trophy },
];
